import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@chakra-ui/react';
import { antifragilityEndpoints } from '../../../endpoints/antifragility';
import { AuthContext } from '../../../providers/authProvider';
import { ShowByRole } from '../../../shared/ShowByRole';
import { AFI_ALERT_LABELS, AFI_ALERT_SEVERITY_COLORS } from '../../constants/afi';
import { IAFINotification } from '../../../models/afi';
import AFIScoreBadge from './AFIScoreBadge';
import { AFIRadarChart } from './AFIRadarChart';
import { AFITrendChart } from './AFITrendChart';
import styles from './AFIAthleteView.module.scss';

interface AFIAthleteViewProps {
  teamId: string;
  athleteId: string;
  athleteName: string;
  onGoToComponent: (component: 'A' | 'B' | 'C') => void;
}

const AFIAthleteView: React.FC<AFIAthleteViewProps> = ({
  teamId,
  athleteId,
  athleteName,
  onGoToComponent,
}) => {
  const authCtx = useContext(AuthContext);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['afi', 'athlete', athleteId, teamId],
    queryFn: async () => {
      const { data } = await antifragilityEndpoints.getAthleteAFI(athleteId, { teamId });
      return data;
    },
    enabled: !!athleteId && !!teamId,
  });

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Spinner size="lg" color="#5BC9E0" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.error}>
        Impossibile caricare dati AFI
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h3 className={styles.athleteName}>{athleteName}</h3>
      </div>

      <div className={styles.mainGrid}>
        {/* Colonna sinistra: radar + pulsanti drill-down */}
        <div className={styles.leftCol}>
          <AFIRadarChart
            componentA={data.componentA}
            componentB={data.componentB}
            componentC={data.componentC}
            showComponentB={authCtx.isAdmin}
            height={240}
          />

          <div className={styles.drillButtons}>
            <button
              className={styles.drillBtn}
              onClick={() => onGoToComponent('A')}
            >
              Dettaglio A
            </button>
            <ShowByRole roles={['isAdmin']}>
              <button
                className={styles.drillBtn}
                onClick={() => onGoToComponent('B')}
              >
                Dettaglio B
              </button>
            </ShowByRole>
            <button
              className={styles.drillBtn}
              onClick={() => onGoToComponent('C')}
            >
              Dettaglio C
            </button>
          </div>
        </div>

        {/* Colonna destra: score card */}
        <div className={styles.rightCol}>
          <div className={styles.scoreCard}>
            <div className={styles.scoreCardTitle}>Score AFI</div>
            <AFIScoreBadge score={data.afiScore} size="lg" showLabel />

            <div className={styles.componentsRow}>
              <div className={styles.compItem}>
                <span className={styles.compLabel}>Comp. A</span>
                <span className={styles.compValue}>
                  {data.componentA !== null ? data.componentA : 'N/D'}
                </span>
              </div>

              <ShowByRole roles={['isAdmin']}>
                <div className={styles.compItem}>
                  <span className={styles.compLabel}>Comp. B</span>
                  <span className={styles.compValue}>
                    {data.componentB !== null ? data.componentB : 'N/D'}
                  </span>
                </div>
              </ShowByRole>

              <div className={styles.compItem}>
                <span className={styles.compLabel}>Comp. C</span>
                <span className={styles.compValue}>
                  {data.componentC !== null ? data.componentC : 'N/D'}
                </span>
              </div>
            </div>

            <div className={styles.detailBtns}>
              <button
                className={styles.detailBtn}
                onClick={() => onGoToComponent('A')}
              >
                Dettaglio A
              </button>
              <ShowByRole roles={['isAdmin']}>
                <button
                  className={styles.detailBtn}
                  onClick={() => onGoToComponent('B')}
                >
                  Dettaglio B
                </button>
              </ShowByRole>
              <button
                className={styles.detailBtn}
                onClick={() => onGoToComponent('C')}
              >
                Dettaglio C
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Trend chart sotto le due colonne */}
      <div className={styles.trendSection}>
        <AFITrendChart data={data.trendHistory ?? []} />
      </div>

      {/* Notifiche */}
      {data.notifications && data.notifications.length > 0 && (
        <div className={styles.notificationsSection}>
          <h4 className={styles.notificationsTitle}>Notifiche</h4>
          <ul className={styles.notificationsList}>
            {data.notifications.map((notif: IAFINotification, idx: number) => (
              <li
                key={idx}
                className={styles.notificationItem}
                style={{ borderLeftColor: AFI_ALERT_SEVERITY_COLORS[notif.severity] }}
              >
                <div className={styles.notifHeader}>
                  <span
                    className={styles.notifType}
                    style={{ color: AFI_ALERT_SEVERITY_COLORS[notif.severity] }}
                  >
                    {AFI_ALERT_LABELS[notif.type]}
                  </span>
                  <span className={styles.notifDate}>{notif.date}</span>
                </div>
                <p className={styles.notifMessage}>{notif.message}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AFIAthleteView;
